import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLoader } from "../context/LoaderContext";
import { useToast } from "./useToast";
import { generateIncomeVerificationUrl } from "../api/services/incomeVerification.service";
import { digitapIncomeUpdateStatus } from "../api/services/digitapIncomeUpdateStatus.service";
import { getBoosterBackRoute } from "../utils/getBoosterBackRoute";
import useAppStore from "../store/useAppStore";
import { Native } from "../platform/android/nativeBridge";

// Global script: Handles closing the popup window on Desktop
const searchParams = new URLSearchParams(window.location.search);
if (searchParams.get("isPopup") === "true") {
  const result = searchParams.get("result");
  const transactionId = searchParams.get("transactionId");
  if (result && transactionId) {
    localStorage.setItem(
      "INCOME_VERIFICATION_EVENT",
      JSON.stringify({ result, transactionId, timestamp: Date.now() })
    );
    window.close();
  }
}

export const useIncomeVerification = () => {
  const navigate = useNavigate();
  const showToast = useToast();
  const location = useLocation();

  const { startLoader, stopLoader } = useLoader();
  const [isProcessing, setIsProcessing] = useState(false);

  const boosterSource = useAppStore((state) => state.boosterSource);

  const handleIncomeVerification = useCallback(
    async (type) => {
      if (isProcessing) return;
      try {
        setIsProcessing(true);
        startLoader("Redirecting to secure verification...");
        sessionStorage.setItem("incomeVerificationFlow", "started");
        const isDesktop = !Native.isApp;

        const payload = {
          properties: {
            stage: boosterSource,
            type,
            return_url: `${window.location.origin}${window.location.pathname}?transactionId=%s&result=%s${isDesktop ? "&isPopup=true" : ""}`,
          },
        };

        const res = await generateIncomeVerificationUrl(payload);
        if (!res?.results?.url) {
          throw new Error("Invalid verification URL");
        }

        if (isDesktop) {
          window.open(res.results.url, "IncomeVerification");
          setTimeout(() => {
            stopLoader();
            setIsProcessing(false);
          }, 2000);
        } else {
          window.location.replace(res.results.url);
        }
      } catch (err) {
        stopLoader();
        setIsProcessing(false);
        showToast(err.message || "Something went wrong", { type: "error" });
      }
    },
    [isProcessing, boosterSource, startLoader, stopLoader, showToast]
  );

  // Listener for popup completion
  useEffect(() => {
    const handleStorageEvent = (e) => {
      if (e.key === "INCOME_VERIFICATION_EVENT" && e.newValue) {
        const data = JSON.parse(e.newValue);
        if (data.result && data.transactionId) {
          navigate(
            `${location.pathname}?result=${data.result}&transactionId=${data.transactionId}`,
            { replace: true }
          );
        }
        localStorage.removeItem("INCOME_VERIFICATION_EVENT");
      }
    };
    window.addEventListener("storage", handleStorageEvent);
    return () => window.removeEventListener("storage", handleStorageEvent);
  }, [location.pathname, navigate]);

  // Mobile callback handler
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const result = urlParams.get("result");
    const transactionId = urlParams.get("transactionId");
    const flowStarted = sessionStorage.getItem("incomeVerificationFlow");

    if (!flowStarted || !result) return;

    startLoader("Verifying income details...");

    const paramsObj = Object.fromEntries(urlParams.entries());

    digitapIncomeUpdateStatus(paramsObj)
      .then(() => {
        if (result === "true" && transactionId) {
          sessionStorage.removeItem("incomeVerificationFlow");
          navigate(getBoosterBackRoute(), { replace: true });
          return;
        }

        showToast("Income verification failed. Please try again.", {
          type: "error",
        });

        navigate(location.pathname, { replace: true });
        setIsProcessing(false);
      })
      .catch((err) => {
        showToast(err?.message || "Income verification failed. Please try again.", {
          type: "error",
        });

        navigate(location.pathname, { replace: true });
        setIsProcessing(false);
      })
      .finally(() => {
        sessionStorage.removeItem("incomeVerificationFlow");
        stopLoader();
      });
  }, [location.pathname, location.search, navigate, showToast, startLoader, stopLoader]);

  return {
    handleIncomeVerification,
    isProcessing,
  };
};
